import React, { useCallback, useEffect, useMemo, useState } from "react";
import { importacionOperacionesApi as api } from "../importacion-operaciones/importacionOperacionesApi";
import "./auditoria-clover.css";

// Espejo de la auditoria Fiserv, pero del universo CLOVER. Responde "que cobro Clover", sin
// cruzar nada: una fila por pago, tenga o no contraparte en la liquidacion.
//
// Lo que NO hace, a proposito: completar con datos de Fiserv. Si Clover no informa cuotas, la
// fila dice que no las informa. Dar por sentado que la ausencia del campo es "1 pago" fue un
// error que ya cometimos; aca esas operaciones tienen su propia categoria y su propia tarjeta.

export const TABS = [
  { id: "TODAS", label: "Todas" },
  { id: "VISA_DEBITO", label: "Visa débito" },
  { id: "MASTERCARD_DEBITO", label: "Mastercard débito" },
  { id: "MAESTRO", label: "Maestro" },
  { id: "VISA", label: "Visa" },
  { id: "MASTERCARD", label: "Mastercard" },
  { id: "AMEX", label: "Amex" },
  { id: "CABAL", label: "Cabal" },
  { id: "QR", label: "QR" },
  { id: "NARANJA", label: "Naranja" },
  { id: "PREPAGAS", label: "Prepagas" },
  { id: "OTRAS", label: "Otras" },
];

export const SUBFILTROS = [
  { id: "", label: "Todas" },
  { id: "DEBIT", label: "Débito" },
  { id: "CREDIT_ONE_PAYMENT", label: "1 pago" },
  { id: "CREDIT_INSTALLMENTS", label: "Cuotas" },
  { id: "CREDIT_INSTALLMENTS_UNKNOWN", label: "Crédito sin dato" },
  { id: "QR", label: "QR" },
  { id: "PREPAID", label: "Prepaga" },
];

const COLUMNAS_ORDENABLES = [
  { id: "fechaoperacion", label: "Fecha" },
  { id: "comercio", label: "Comercio" },
  { id: "terminal", label: "Terminal" },
  { id: "cuenta", label: "Cuenta" },
  { id: "importe", label: "Importe" },
  { id: "marca", label: "Marca" },
  { id: "cuotas", label: "Cuotas" },
  { id: "cupon", label: "Recibo" },
  { id: "lote", label: "Lote" },
];

const TAMANOS = [50, 100, 250, 500];

export const formatearImporte = (valor) =>
  typeof valor === "number"
    ? valor.toLocaleString("es-AR", { style: "currency", currency: "ARS", minimumFractionDigits: 2 })
    : "—";

export const formatearFecha = (valor) => {
  if (!valor) return "—";
  const fecha = new Date(valor);
  if (Number.isNaN(fecha.getTime())) return "—";
  const dd = String(fecha.getDate()).padStart(2, "0");
  const mm = String(fecha.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${fecha.getFullYear()}`;
};

// Sin dato se muestra "s/d", nunca 1. Un 1 inventado se lee como "venta en un pago", que es
// justo lo que no sabemos cuando el campo no vino.
export const formatearCuotas = (fila) => {
  if (!fila) return "—";
  if (!fila.installmentsKnown) return "s/d";
  return fila.installments ?? "—";
};

export const describirRango = (page, pageSize, total) => {
  if (!total) return "Sin operaciones";
  const desde = (page - 1) * pageSize + 1;
  const hasta = Math.min(page * pageSize, total);
  return `Mostrando ${desde}–${hasta} de ${total} operaciones`;
};

const FILTROS_VACIOS = {
  operationDate: "", commerce: "", terminal: "", coupon: "", batch: "",
  authorization: "", accountKey: "", entryType: "", installments: "",
  minAmount: "", maxAmount: "", search: "", onlyEmvDiscrepancy: "",
};

function Card({ titulo, valor, importe, alerta }) {
  return (
    <div className={`acl-card ${alerta ? "acl-card--alerta" : ""}`} data-testid={`acl-card-${titulo}`}>
      <span className="acl-card__titulo">{titulo}</span>
      <strong className="acl-card__valor">{valor}</strong>
      {importe !== undefined && <span className="acl-card__importe">{formatearImporte(importe)}</span>}
    </div>
  );
}

function Detalle({ fila }) {
  const rawFields = Array.isArray(fila.rawFields) ? fila.rawFields : [];
  return (
    <tr className="acl-detalle" data-testid={`acl-detalle-${fila.paymentId}`}>
      <td colSpan={15}>
        <div className="acl-detalle__grid">
          <section>
            <h5>Identificación</h5>
            <dl>
              <dt>paymentId</dt><dd>{fila.paymentId || "—"}</dd>
              <dt>merchantId</dt><dd>{fila.merchantId || "—"}</dd>
              <dt>Cuenta</dt><dd>{fila.accountKey || "—"}</dd>
              <dt>Fuente</dt><dd>{fila.fuente || "—"}</dd>
            </dl>
          </section>
          <section>
            <h5>Operación</h5>
            <dl>
              <dt>Fecha</dt><dd>{formatearFecha(fila.fechaOperacion)}</dd>
              <dt>Comercio</dt><dd>{fila.comercio || "—"}</dd>
              <dt>Terminal</dt><dd>{fila.terminal || "—"}</dd>
              <dt>Recibo</dt><dd>{fila.cupon || "—"}</dd>
              <dt>Lote</dt><dd>{fila.lote || "—"}</dd>
              <dt>Autorización</dt><dd>{fila.autorizacion || "—"}</dd>
            </dl>
          </section>
          <section>
            <h5>Instrumento</h5>
            <dl>
              <dt>Medio de pago</dt><dd>{fila.paymentMethod || "—"}</dd>
              <dt>Marca</dt><dd>{fila.marca || "—"}</dd>
              <dt>cardType</dt><dd>{fila.cardType || "—"}</dd>
              <dt>Últimos 4</dt><dd>{fila.last4 || "—"}</dd>
              <dt>Método de ingreso</dt><dd>{fila.entryType || "—"}</dd>
            </dl>
          </section>
          <section>
            <h5>Producto</h5>
            <dl>
              <dt>productType</dt><dd>{fila.productType || "—"}</dd>
              <dt>Cuotas</dt><dd>{formatearCuotas(fila)}</dd>
              <dt>Cuotas informadas</dt><dd>{fila.installmentsKnown ? "Sí" : "No"}</dd>
              <dt>Estado</dt><dd>{fila.estado || "—"}</dd>
            </dl>
          </section>
          <section>
            <h5>Diagnóstico EMV</h5>
            <dl>
              <dt>paymentMethod</dt><dd>{fila.paymentMethod || "—"}</dd>
              <dt>cardTypeLabel</dt><dd>{fila.marca || "—"}</dd>
              <dt>applicationLabel</dt><dd>{fila.applicationLabel || "no informado"}</dd>
              <dt>Clase actual</dt><dd>{fila.productType || "—"}</dd>
              <dt>Clase sugerida</dt><dd>{fila.emvSuggestedClass || "sin sugerencia"}</dd>
              <dt>Override sugerido</dt><dd>{fila.emvOverrideSuggested ? "Sí" : "No"}</dd>
              <dt>Familia Fiserv</dt><dd>{fila.fiservFamily || "sin contraparte"}</dd>
              <dt>Consumida por matcher</dt>
              <dd>
                {fila.consumedByMatcher === null || fila.consumedByMatcher === undefined
                  ? "—"
                  : fila.consumedByMatcher ? "Sí" : "No"}
              </dd>
            </dl>
          </section>
        </div>
        <section className="acl-raw" data-testid={`acl-raw-${fila.paymentId}`}>
          <h5>Todos los campos Clover v3 ({rawFields.length})</h5>
          {rawFields.length > 0 ? (
            <div className="acl-raw__scroll">
              <table>
                <thead><tr><th>Campo</th><th>Valor</th></tr></thead>
                <tbody>
                  {rawFields.map((field, index) => (
                    <tr key={`${field.path}-${index}`}>
                      <td><code>{field.path}</code></td>
                      <td>{field.value === "" ? "(vacío)" : String(field.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="acl-nota">Este pago todavía no tiene el payload v3 persistido.</p>}
        </section>
      </td>
    </tr>
  );
}

export default function AuditoriaClover({ runId, token, darkMode }) {
  const [datos, setDatos] = useState(null),
    [cargando, setCargando] = useState(false),
    [error, setError] = useState(""),
    [colapsado, setColapsado] = useState(true),
    [tab, setTab] = useState("TODAS"),
    [producto, setProducto] = useState(""),
    [filtros, setFiltros] = useState(FILTROS_VACIOS),
    [orden, setOrden] = useState({ by: "fechaoperacion", desc: false }),
    [page, setPage] = useState(1),
    [pageSize, setPageSize] = useState(100),
    [abierta, setAbierta] = useState("");

  const consulta = useMemo(
    () => ({
      tab, productType: producto, ...filtros,
      sortBy: orden.by, sortDescending: orden.desc, page, pageSize,
    }),
    [tab, producto, filtros, orden, page, pageSize],
  );

  const cargar = useCallback(async () => {
    if (!runId || colapsado) return;
    setCargando(true);
    setError("");
    try {
      setDatos(await api.obtenerOperacionesClover(runId, token, consulta));
    } catch (e) {
      setDatos(null);
      setError(e?.message || "No se pudo cargar la auditoría Clover.");
    } finally {
      setCargando(false);
    }
  }, [runId, token, consulta, colapsado]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { setPage(1); }, [tab, producto, filtros, pageSize]);
  useEffect(() => { setDatos(null); setTab("TODAS"); setFiltros(FILTROS_VACIOS); }, [runId]);

  const cambiarFiltro = (campo, valor) => setFiltros((prev) => ({ ...prev, [campo]: valor }));
  const ordenarPor = (campo) =>
    setOrden((prev) => ({ by: campo, desc: prev.by === campo ? !prev.desc : false }));

  const filas = datos?.rows || [];
  const s = datos?.filteredSummary;
  const tabs = datos?.tabs || [];
  const cuentaDe = (id) => tabs.find((t) => t.tab === id)?.operations ?? 0;
  const totalPaginas = s ? Math.max(1, Math.ceil(datos.filteredCount / pageSize)) : 1;

  if (!runId) return null;

  return (
    <section className={`acl ${darkMode ? "acl--dark" : ""}`} data-testid="auditoria-clover">
      <header className="acl__header">
        <div>
          <h3>Auditoría de operaciones Clover</h3>
          <p className="acl-nota">Lo que informa Clover, sin cruzar con Fiserv</p>
        </div>
        <button
          type="button"
          className="acl__toggle"
          onClick={() => setColapsado((v) => !v)}
          data-testid="acl-toggle"
          aria-expanded={!colapsado}
        >
          {colapsado ? "Mostrar detalle" : "Ocultar detalle"}
        </button>
        {!colapsado && (
          <a
            className="acl__export"
            href={api.buildExportOperacionesCloverUrl(runId, token, consulta)}
            data-testid="acl-export"
          >
            Exportar vista
          </a>
        )}
      </header>

      {colapsado && <p className="acl-nota">Bloque colapsado. Abrilo para auditar operación por operación.</p>}

      {!colapsado && (
        <>
          {error && <p className="acl-error" role="alert" data-testid="acl-error">{error}</p>}
          {cargando && !datos && <p data-testid="acl-cargando">Cargando auditoría…</p>}

          {!error && datos && s && (
            <>
              <div className="acl-cards">
                <Card titulo="Operaciones" valor={s.operations} importe={s.grossAmount} />
                <Card titulo="Débito" valor={s.debitCount} importe={s.debitAmount} />
                <Card titulo="Crédito 1 pago" valor={s.creditOnePaymentCount} importe={s.creditOnePaymentAmount} />
                <Card titulo="Cuotas" valor={s.installmentsCount} importe={s.installmentsAmount} />
                <Card
                  titulo="Crédito sin dato de cuotas"
                  valor={s.creditUnknownInstallmentsCount}
                  importe={s.creditUnknownInstallmentsAmount}
                  alerta={s.creditUnknownInstallmentsCount > 0}
                />
                <Card titulo="QR" valor={s.qrCount} importe={s.qrAmount} />
                <Card titulo="Prepaga" valor={s.prepaidCount} importe={s.prepaidAmount} />
                <Card titulo="Otros" valor={s.otherCount} importe={s.otherAmount} />
              </div>

              {/* La cobertura del dato de cuotas condiciona toda la lectura de esta pantalla:
                  con cobertura baja, la separacion 1 pago / cuotas no se puede sostener. */}
              <div className="acl-cobertura" data-testid="acl-cobertura">
                <strong>Cobertura de cuotas</strong>
                <span>Crédito: {s.creditCount}</span>
                <span>Con dato: {s.creditOnePaymentCount + s.installmentsCount}</span>
                <span>Sin dato: {s.creditUnknownInstallmentsCount}</span>
                <span
                  className={`acl-estado acl-estado--${
                    s.installmentsCoverage === null || s.installmentsCoverage === undefined
                      ? "neutro"
                      : s.installmentsCoverage >= 99
                        ? "ok"
                        : "alerta"
                  }`}
                >
                  {s.installmentsCoverage === null || s.installmentsCoverage === undefined
                    ? "SIN CRÉDITO"
                    : `${s.installmentsCoverage} %`}
                </span>
                {s.creditUnknownInstallmentsCount > 0 && (
                  <span className="acl-nota">
                    Clover no informó cuotas en esas operaciones. No se asume 1 pago.
                  </span>
                )}
              </div>

              {/* Diagnóstico, no decisión: la clase efectiva de cada fila sigue siendo la
                  actual. Esto solo cuenta cuántas corregiría el chip y con cuánto respaldo. */}
              {s.emvOverrideCandidates > 0 && (
                <div className="acl-emv" data-testid="acl-emv-resumen">
                  <strong>Diagnóstico EMV</strong>
                  <span>Con etiqueta del chip: {s.emvLabelCount}</span>
                  <span>Overrides candidatos: {s.emvOverrideCandidates}</span>
                  <span>Confirmados por Fiserv: {s.emvOverrideConfirmedByFiserv}</span>
                  <span>Sin evidencia Fiserv: {s.emvOverrideWithoutFiserv}</span>
                  <span className="acl-nota">
                    Solo diagnóstico: la clasificación efectiva no cambia.
                  </span>
                </div>
              )}

              <nav className="acl-tabs" role="tablist">
                {TABS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={tab === t.id}
                    className={tab === t.id ? "activo" : ""}
                    onClick={() => setTab(t.id)}
                  >
                    {t.label} <span className="acl-tabs__cuenta">{cuentaDe(t.id)}</span>
                  </button>
                ))}
              </nav>

              <nav className="acl-subtabs">
                {SUBFILTROS.map((f) => (
                  <button
                    key={f.id || "todas"}
                    type="button"
                    className={producto === f.id ? "activo" : ""}
                    onClick={() => setProducto(f.id)}
                  >
                    {f.label}
                  </button>
                ))}
              </nav>

              <div className="acl-filtros">
                <label>
                  Fecha
                  <input
                    type="date"
                    value={filtros.operationDate}
                    onChange={(e) => cambiarFiltro("operationDate", e.target.value)}
                    data-testid="acl-filtro-fecha"
                  />
                </label>
                <label>
                  Comercio
                  <input
                    value={filtros.commerce}
                    onChange={(e) => cambiarFiltro("commerce", e.target.value)}
                    data-testid="acl-filtro-comercio"
                  />
                </label>
                <label>
                  Terminal
                  <input
                    value={filtros.terminal}
                    onChange={(e) => cambiarFiltro("terminal", e.target.value)}
                    data-testid="acl-filtro-terminal"
                  />
                </label>
                <label>
                  Recibo
                  <input value={filtros.coupon} onChange={(e) => cambiarFiltro("coupon", e.target.value)} />
                </label>
                <label>
                  Lote
                  <input value={filtros.batch} onChange={(e) => cambiarFiltro("batch", e.target.value)} />
                </label>
                <label>
                  Autorización
                  <input value={filtros.authorization} onChange={(e) => cambiarFiltro("authorization", e.target.value)} />
                </label>
                <label>
                  Cuenta
                  <select
                    value={filtros.accountKey}
                    onChange={(e) => cambiarFiltro("accountKey", e.target.value)}
                    data-testid="acl-filtro-cuenta"
                  >
                    <option value="">Todas</option>
                    {Object.keys(s.byAccount || {}).map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Método de ingreso
                  <select value={filtros.entryType} onChange={(e) => cambiarFiltro("entryType", e.target.value)}>
                    <option value="">Todos</option>
                    {Object.keys(s.byEntryType || {}).map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Cuotas
                  <input
                    className="acl-cuotas"
                    type="number"
                    min="1"
                    value={filtros.installments}
                    onChange={(e) => cambiarFiltro("installments", e.target.value)}
                    data-testid="acl-filtro-cuotas"
                  />
                </label>
                <label>
                  Importe desde
                  <input
                    className="acl-cuotas"
                    type="number"
                    value={filtros.minAmount}
                    onChange={(e) => cambiarFiltro("minAmount", e.target.value)}
                    data-testid="acl-filtro-monto"
                  />
                </label>
                <label>
                  Importe hasta
                  <input
                    className="acl-cuotas"
                    type="number"
                    value={filtros.maxAmount}
                    onChange={(e) => cambiarFiltro("maxAmount", e.target.value)}
                  />
                </label>
                <label className="acl-filtros__buscador">
                  Buscar
                  <input
                    placeholder="comercio, recibo, lote, autorización, paymentId…"
                    value={filtros.search}
                    onChange={(e) => cambiarFiltro("search", e.target.value)}
                    data-testid="acl-filtro-buscar"
                  />
                </label>
                <label className="acl-check">
                  <input
                    type="checkbox"
                    checked={filtros.onlyEmvDiscrepancy === "true"}
                    onChange={(e) => cambiarFiltro("onlyEmvDiscrepancy", e.target.checked ? "true" : "")}
                    data-testid="acl-filtro-emv"
                  />
                  Solo discrepancias EMV (crédito → débito)
                </label>
                <button type="button" onClick={() => setFiltros(FILTROS_VACIOS)} data-testid="acl-limpiar">
                  Limpiar
                </button>
              </div>

              <div className="acl-barra">
                <span data-testid="acl-rango">{describirRango(page, pageSize, datos.filteredCount)}</span>
                <strong>{formatearImporte(datos.filteredGrossAmount)}</strong>
                <label>
                  Filas
                  <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                    {TAMANOS.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
                <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  Anterior
                </button>
                <button type="button" disabled={page >= totalPaginas} onClick={() => setPage((p) => p + 1)}>
                  Siguiente
                </button>
              </div>

              <div className="acl-scroll">
                <table className="acl-tabla">
                  <thead>
                    <tr>
                      {COLUMNAS_ORDENABLES.map((c) => (
                        <th key={c.id}>
                          <button type="button" onClick={() => ordenarPor(c.id)}>
                            {c.label}{orden.by === c.id ? (orden.desc ? " ↓" : " ↑") : ""}
                          </button>
                        </th>
                      ))}
                      <th>Medio de pago</th>
                      <th>Producto</th>
                      <th>Ingreso</th>
                      <th>Estado</th>
                      <th>Últimos 4</th>
                      <th>EMV</th>
                      <th>Sugerido</th>
                      <th>Campos v3</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filas.map((fila) => (
                      <React.Fragment key={fila.paymentId}>
                        <tr
                          className="acl-fila"
                          onClick={() => setAbierta((v) => (v === fila.paymentId ? "" : fila.paymentId))}
                          data-testid={`acl-fila-${fila.paymentId}`}
                        >
                          <td>{formatearFecha(fila.fechaOperacion)}</td>
                          <td>{fila.comercio || "—"}</td>
                          <td>{fila.terminal || "—"}</td>
                          <td>{fila.accountKey || "—"}</td>
                          <td className="acl-num">{formatearImporte(fila.importe)}</td>
                          <td>{fila.marca || "—"}</td>
                          <td className={`acl-num ${fila.installmentsKnown ? "" : "acl-sin-dato"}`}>
                            {formatearCuotas(fila)}
                          </td>
                          <td className="acl-num">{fila.cupon || "—"}</td>
                          <td className="acl-num">{fila.lote || "—"}</td>
                          <td>{fila.paymentMethod || "—"}</td>
                          <td>{fila.productType || "—"}</td>
                          <td>{fila.entryType || "—"}</td>
                          <td>{fila.estado || "—"}</td>
                          <td>{fila.last4 || "—"}</td>
                          <td className={fila.applicationLabel ? "" : "acl-sin-dato"}>
                            {fila.applicationLabel || "s/d"}
                          </td>
                          <td>
                            {fila.emvOverrideSuggested ? (
                              <span className="acl-badge-emv" data-testid={`acl-emv-${fila.paymentId}`}>
                                EMV SUGIERE DÉBITO
                              </span>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="acl-num">{fila.rawFieldCount || 0}</td>
                        </tr>
                        {abierta === fila.paymentId && <Detalle fila={fila} />}
                      </React.Fragment>
                    ))}
                    {filas.length === 0 && (
                      <tr>
                        <td colSpan={17} className="acl-vacio" data-testid="acl-vacio">
                          No hay operaciones con esos filtros.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
}
